import type { Achievement } from '$lib/achievements'
import type { UserAccessory } from '$lib/types'
import { SvelteSet } from 'svelte/reactivity'
import type { Readable } from 'svelte/store'

export type RevealItem =
  | { kind: 'accessory'; id: string; accessory: UserAccessory }
  | { kind: 'achievement'; id: string; achievement: Achievement }

export type RevealQueueDependencies = {
  /** Source stores. Omit them to drive the queue manually via enqueue(). */
  accessories?: Readable<UserAccessory[]>
  achievements?: Readable<Achievement[]>

  markAccessorySeen: (id: string) => Promise<void>
  markAchievementSeen: (id: string) => Promise<void>
  equipAccessory: (accessory: UserAccessory) => Promise<void>
}

export type RevealQueue = ReturnType<typeof createRevealQueue>

/**
 * FIFO queue of pending reveals, merged from the new-accessories and
 * new-achievements stores. One item is presented at a time; the next one only
 * after the current one is dismissed (which persists its seen state).
 *
 * Closing the queue marks the item currently on screen as seen (its ceremony
 * already played), but NOT the queued items that were never shown: those are
 * parked for this app lifetime and re-reveal on the next full page load.
 */
export const createRevealQueue = (dependencies: RevealQueueDependencies) => {
  const queue = $state<RevealItem[]>([])
  // Size of the current batch, so the UI can show "2 of 3" while the queue
  // itself shrinks on every dismissal.
  let total = $state(0)

  // Every item ever enqueued in this app lifetime. The source stores re-emit
  // items we already presented (the achievements store never removes seen
  // items locally, and both stores reload at various triggers), so this set is
  // what prevents duplicate ceremonies.
  const handledKeys = new SvelteSet<string>()

  const keyOf = (item: Pick<RevealItem, 'kind' | 'id'>) =>
    `${item.kind}:${item.id}`

  const enqueue = (item: RevealItem) => {
    const key = keyOf(item)
    if (handledKeys.has(key)) return
    handledKeys.add(key)

    if (queue.length === 0) total = 0
    queue.push(item)
    total += 1
  }

  const unsubscribers: (() => void)[] = []

  if (dependencies.accessories) {
    unsubscribers.push(
      dependencies.accessories.subscribe((accessories) => {
        for (const accessory of accessories) {
          if (accessory.seen) continue
          enqueue({ kind: 'accessory', id: accessory.id, accessory })
        }
      }),
    )
  }

  if (dependencies.achievements) {
    unsubscribers.push(
      dependencies.achievements.subscribe((achievements) => {
        for (const achievement of achievements) {
          enqueue({ kind: 'achievement', id: achievement.id, achievement })
        }
      }),
    )
  }

  // Fire and forget: the ceremony advances immediately, the RPC persists
  // seen_at in the background.
  const markSeen = (item: RevealItem) => {
    if (item.kind === 'accessory') {
      dependencies.markAccessorySeen(item.id)
    } else {
      dependencies.markAchievementSeen(item.id)
    }
  }

  const dismissCurrent = () => {
    const item = queue[0]
    if (!item) return

    markSeen(item)
    queue.shift()
  }

  const equipCurrent = () => {
    const item = queue[0]
    if (item?.kind !== 'accessory') return

    dependencies.equipAccessory(item.accessory)
    dismissCurrent()
  }

  const close = () => {
    // The item on screen already had its ceremony, so it counts as seen. The
    // remaining items stay in handledKeys (added on enqueue), so they won't
    // re-enqueue from store re-emissions during this app lifetime — but their
    // seen_at stays null, so they re-reveal after the next page load.
    const current = queue[0]
    if (current) markSeen(current)
    queue.length = 0
  }

  const destroy = () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe())
  }

  return {
    get current(): RevealItem | undefined {
      return queue[0]
    },
    get visible() {
      return queue.length > 0
    },
    get total() {
      return total
    },
    get position() {
      return total - queue.length + 1
    },
    enqueue,
    dismissCurrent,
    equipCurrent,
    close,
    destroy,
  }
}
